import React, { useEffect, useState, useRef } from 'react';
import {
  useRecordContext,
  useRefresh,
  useDataProvider,
  useNotify,
} from 'react-admin';
import { Box, Alert, Divider, Typography } from '@mui/material';
import { LEAD_IMPORTS_RESOURCE_NAME as RESOURCE_NAME } from '../leadImports.dataProvider';
import { leadImportFields, leadImportStatuses } from '../leadImports.model';
import { ProgressBar } from './field';
import {
  QualityOverview,
  MetricsDisplay,
  FailedRowsDetails,
  DuplicateRowsDetails,
} from './report';
import ProgressLog from './ProgressLog.component';
import logger from '../../utils/logger';
const RealTimeImportDetails = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const intervalRef = useRef(null);
  const [lastStatus, setLastStatus] = useState(null);

  // Determine if we should poll based on status
  const shouldPoll =
    record &&
    [leadImportStatuses.QUEUED, leadImportStatuses.PROCESSING, leadImportStatuses.ROLLING_BACK].includes(
      record[leadImportFields.status],
    );

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (shouldPoll) {
      logger.log(
        `Starting polling for batch ${record[leadImportFields.id]} with status ${record[leadImportFields.status]}`,
      );

      intervalRef.current = setInterval(async () => {
        try {
          logger.log(
            `Polling for batch ${record[leadImportFields.id]} status...`,
          );
          const response = await dataProvider.getOne(RESOURCE_NAME, {
            id: record[leadImportFields.id],
          });

          logger.log('Polling response:', response.data);
          const newStatus = response.data[leadImportFields.status];

          if (newStatus !== lastStatus) {
            setLastStatus(newStatus);

            // If status changed to completed or failed, stop polling
            if (
              newStatus === leadImportStatuses.COMPLETED ||
              newStatus === leadImportStatuses.FAILED ||
              newStatus === leadImportStatuses.ROLLED_BACK ||
              newStatus === leadImportStatuses.ROLLBACK_FAILED
            ) {
              logger.log(
                `Import finished with status: ${newStatus}. Stopping polling.`,
              );

              // Clear interval
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }

              // Final refresh to get updated data
              refresh();

              // Show notification
              if (newStatus === leadImportStatuses.COMPLETED) {
                notify('Import completed successfully!', { type: 'success' });
              } else if (newStatus === leadImportStatuses.ROLLED_BACK) {
                notify('Rollback completed successfully.', { type: 'success' });
              } else if (newStatus === leadImportStatuses.ROLLBACK_FAILED) {
                notify('Rollback failed. Check details for more info.', { type: 'error' });
              } else {
                notify('Import failed. Check the error log for details.', {
                  type: 'error',
                });
              }
            } else {
              // Status is still processing, just refresh
              refresh();
            }
          }
        } catch (error) {
          logger.error('Polling error:', error);
          // Continue polling on error, but don't spam notifications
        }
      }, 3000);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        logger.log('Cleaning up polling interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [record?.[leadImportFields.id], shouldPoll]); // Only depend on ID and shouldPoll

  if (!record) return null;

  const stats = record[leadImportFields.stats];
  const isCompleted =
    record[leadImportFields.status] === leadImportStatuses.COMPLETED;
  const isFailed =
    record[leadImportFields.status] === leadImportStatuses.FAILED;
  const isProcessing =
    record[leadImportFields.status] === leadImportStatuses.PROCESSING;
  const isQueued =
    record[leadImportFields.status] === leadImportStatuses.QUEUED;
  const isRolledBack =
    record[leadImportFields.status] === leadImportStatuses.ROLLED_BACK;
  const isRollingBack =
    record[leadImportFields.status] === leadImportStatuses.ROLLING_BACK;
  const isRollbackFailed =
    record[leadImportFields.status] === leadImportStatuses.ROLLBACK_FAILED;

  logger.log('record', record);

  return (
    <Box>
      {/* Real-time processing alerts */}
      {(isProcessing || isQueued) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Import in progress... Refreshing automatically every 3 seconds.
        </Alert>
      )}

      {isFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Import failed. Please check the error details below for more
          information.
        </Alert>
      )}

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Import completed successfully! Review the quality assessment and
          details below.
        </Alert>
      )}

      {isRollingBack && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Rollback in progress... Refreshing automatically every 3 seconds.
        </Alert>
      )}

      {isRolledBack && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Import has been rolled back. All active leads from this import have been removed.
        </Alert>
      )}

      {isRollbackFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Rollback failed. Some leads may have been partially removed. Check error details below.
        </Alert>
      )}


      {/* Quality Overview - New enhanced component */}
      {stats && isCompleted && <QualityOverview />}

      {/* Detailed metrics for completed imports */}
      {isCompleted && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Detailed Processing Information
          </Typography>
          <MetricsDisplay variant="detailed" />
        </>
      )}

      {/* Progress log */}
      <ProgressLog />


    </Box>
  );
};

export default RealTimeImportDetails;
